<script lang="ts">
    import { useFragment } from '$gql/fragment-masking.js';
    import {
        MediaImageFragmentDoc,
        type ParticipantAvatarFragment,
    } from '$gql/graphql.js';
    import { cx } from '$lib/utils/cx.js';
    import MediaImageOrPlaceholder from '$components/MediaImage/MediaImageOrPlaceholder.svelte';
    import { match, P } from 'ts-pattern';
    import { participantSveltekitReload } from '$lib/utils/participantUtils';

    export let participant: ParticipantAvatarFragment;

    export let size:
        | 'full'
        | 'xs'
        | 'sm'
        | 'md'
        | 'lg'
        | 'xl'
        | number
        | [number, number] = 'md';
    export let format: string | null | undefined = undefined;

    let className: string = '';
    export { className as class };

    export let imgClass: string = '';
    export let dontWrapInLink: boolean = false;

    $: media = useFragment(MediaImageFragmentDoc, participant?.participantPhoto);
    $: imgSizes = match(size)
        .with('full', () => ['100%', '100%'])
        .with('xl', () => [50, 60])
        .with('lg', () => [45, 55])
        .with('md', () => [40, 50])
        .with('sm', () => [35, 40])
        .with('xs', () => [20, 24])
        .with(P.number.select(), (n) => [n, n])
        .with([P.select('mobileSize'), P.select('size')], ({ mobileSize, size }) => [
            mobileSize,
            size,
        ])
        .exhaustive();

    function getFormat(
        format: string | null | undefined,
        _imgSizes: typeof imgSizes,
    ): string {
        if (format != null) {
            return format;
        }

        return match(_imgSizes)
            .with([P._, P.number.select()], (size) => {
                return size < 50 ? 'w100' : 'w200';
            })
            .otherwise(() => 'w200');
    }

    $: format_ = getFormat(format, imgSizes);

    $: imgClass_ = cx(
        'rounded-full bg-gray-300 object-cover shrink-0 force-size-[--mobile-size] t:force-size-[--size]',
        imgClass,
    );

    $: mobileSize = match(imgSizes)
        .with([P.number.select(), P._], (size) => {
            return `${size}px`;
        })
        .with(['100%', P._], () => {
            return '100%';
        })
        .otherwise(() => '100%');

    $: size_ = match(imgSizes)
        .with([P._, P.number.select()], (size) => {
            return `${size}px`;
        })
        .with([P._, '100%'], () => {
            return '100%';
        })
        .otherwise(() => '100%');

    $: sveltekitReload = participantSveltekitReload(participant);
</script>

{#if dontWrapInLink}
    <MediaImageOrPlaceholder
        class={imgClass_}
        mediaId={media?.id ?? null}
        alt={`${participant?.participantName}`}
        format={format_}
        --mobile-size={mobileSize}
        --size={size_}
    />
{:else}
    <a
        data-sveltekit-reload={sveltekitReload}
        href={participant?.participantProfileLink}
        class={className}
    >
        <MediaImageOrPlaceholder
            class={imgClass_}
            mediaId={media?.id ?? null}
            alt={`${participant?.participantName}`}
            format={format_}
            --mobile-size={mobileSize}
            --size={size_}
        />
        <span class="sr-only">{participant?.participantName}</span>
    </a>
{/if}
